
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router';
import { supabase } from '@/integrations/supabase/client';
import { UserProfile } from '@/contexts/auth/types';

// Extended UserProfile with membership data
export interface UserWithMembership extends UserProfile {
  role: 'admin' | 'manager' | 'staff';
  organization_id: string;
  joined_at: string;
  membership_is_active: boolean;
  email?: string;
}

export const useUsers = () => {
  const { currentUserRole, currentOrganization } = useAuth();
  const navigate = useNavigate();

  // Check if user is admin and redirect if not
  if (currentUserRole && currentUserRole !== 'admin') {
    navigate('/');
  }

  const {
    data: users = [],
    isLoading,
    error,
    refetch: fetchUsers,
  } = useQuery({
    queryKey: ['users', currentOrganization?.id],
    queryFn: async (): Promise<UserWithMembership[]> => {
      if (!currentOrganization) {
        throw new Error('No organization selected');
      }

      console.log('Fetching users for organization:', currentOrganization.id);

      // First get the organization memberships (both active and inactive)
      const { data: memberships, error: membershipError } = await supabase
        .from('user_organization_memberships')
        .select('user_id, role, joined_at, organization_id, is_active')
        .eq('organization_id', currentOrganization.id)
        .order('joined_at', { ascending: false });

      console.log('Memberships fetched:', memberships);

      if (membershipError) {
        console.error('Error fetching memberships:', membershipError);
        throw membershipError;
      }

      if (!memberships || memberships.length === 0) {
        return [];
      }

      // Get user IDs from memberships
      const userIds = memberships.map(m => m.user_id);

      // Fetch user profiles for these users
      const { data: profiles, error: profileError } = await supabase
        .from('user_profiles')
        .select('id, user_id, full_name, email, is_active, last_login_at, created_at, updated_at')
        .in('user_id', userIds);

      if (profileError) {
        console.error('Error fetching user profiles:', profileError);
        throw profileError;
      }

      console.log('Fetched memberships:', memberships);
      console.log('Fetched profiles:', profiles);

      // Join the data manually
      const transformedUsers = memberships
        .map(membership => {
          const profile = profiles?.find(p => p.user_id === membership.user_id);
          if (!profile) return null;

          return {
            id: profile.id,
            user_id: profile.user_id,
            full_name: profile.full_name,
            email: profile.email || '',
            is_active: profile.is_active,
            last_login_at: profile.last_login_at,
            created_at: profile.created_at,
            updated_at: profile.updated_at,
            role: membership.role,
            organization_id: membership.organization_id,
            joined_at: membership.joined_at,
            membership_is_active: membership.is_active
          } as UserWithMembership;
        })
        .filter((user): user is UserWithMembership => user !== null);

      return transformedUsers;
    },
    enabled: !!currentOrganization && currentUserRole === 'admin',
  });

  console.log('Users fetched:', users);

  return {
    users,
    loading: isLoading,
    error,
    fetchUsers,
    profile: { role: currentUserRole },
    organization: currentOrganization
  };
};
